import { useLazyQuery, useQuery } from '@apollo/client';
import { useAuth } from '@contexts/AuthContext';
import { useProId } from '@contexts/ProIdContext';
import { GET_JOB_APPLICATIONS_PRO_V2, GET_JOB_APPLICATIONS_UPERCUT } from '@gql/jobs';
import { IJobApplicationDetailFilter } from '@modules/jobApplications/JobApplicationFilter';
import { JobApplicationRaw } from '@modules/jobApplications/types';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    FormControlLabel,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
    ALL_EXPORT_COLUMNS,
    EXPORT_COLUMN_GROUPS,
    ExportColumnGroup,
    buildCsvRows,
    buildFilename,
    downloadCsvFile,
} from './columns';

const EXPORT_PAGE_SIZE = 100;

interface ExportJobApplicationsModalProps {
    open: boolean;
    onClose: () => void;
    jobTitle?: string;
    jobNumber?: string;
    filter?: IJobApplicationDetailFilter;
}

const GROUP_ICONS: Record<ExportColumnGroup, React.ReactNode> = {
    talent: <i className="fi fi-br-mode-portrait" style={{ fontSize: 16, display: 'flex' }} />,
    application: <i className="fi fi-br-inbox" style={{ fontSize: 16, display: 'flex' }} />,
    job: <i className="fi fi-br-briefcase" style={{ fontSize: 16, display: 'flex' }} />,
};

const StepIndicator = ({ currentStep }: { currentStep: 0 | 1 }) => {
    const theme = useTheme();
    const steps = [
        { label: 'Colonnes', icon: <i className="fi fi-br-settings-sliders" style={{ fontSize: 12 }} /> },
        { label: 'Aperçu & Export', icon: <i className="fi fi-br-table-list" style={{ fontSize: 12 }} /> },
    ];

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            {steps.map((step, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '20px',
                            background:
                                idx === currentStep
                                    ? theme.palette.primary.main
                                    : idx < currentStep
                                    ? 'rgba(91, 72, 217, 0.15)'
                                    : 'transparent',
                            transition: 'background 0.2s',
                        }}
                    >
                        <Box
                            sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background:
                                    idx === currentStep
                                        ? 'rgba(255,255,255,0.25)'
                                        : idx < currentStep
                                        ? theme.palette.primary.main
                                        : 'rgba(255,255,255,0.1)',
                                color: '#fff',
                                fontSize: 11,
                                fontWeight: 700,
                                flexShrink: 0,
                            }}
                        >
                            {idx < currentStep ? '✓' : idx + 1}
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: idx === currentStep ? 700 : 500,
                                color:
                                    idx === currentStep
                                        ? '#fff'
                                        : idx < currentStep
                                        ? theme.palette.primary.main
                                        : theme.palette.text.secondary,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {step.label}
                        </Typography>
                    </Box>
                    {idx < steps.length - 1 && (
                        <Box
                            sx={{
                                width: 24,
                                height: 1,
                                background:
                                    idx < currentStep
                                        ? theme.palette.primary.main
                                        : 'rgba(255,255,255,0.12)',
                                mx: 0.5,
                                flexShrink: 0,
                            }}
                        />
                    )}
                </Box>
            ))}
        </Box>
    );
};

const ExportJobApplicationsModal = ({
    open,
    onClose,
    jobTitle,
    jobNumber,
    filter = {},
}: ExportJobApplicationsModalProps) => {
    const theme = useTheme();
    const { user, isUpercut } = useAuth();
    const { isProIdRequired } = useProId();
    const isUpercutView = isUpercut && !isProIdRequired;
    const queryToUse = isUpercutView ? GET_JOB_APPLICATIONS_UPERCUT : GET_JOB_APPLICATIONS_PRO_V2;

    const extractResult = useCallback(
        (data: any) => (isUpercutView ? data?.getJobApplicationsUpercut : data?.getJobApplicationsProV2),
        [isUpercutView],
    );

    const [step, setStep] = useState<0 | 1>(0);
    const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>(() =>
        ALL_EXPORT_COLUMNS.map((c) => c.id),
    );
    const [exportLoading, setExportLoading] = useState(false);
    const [exportProgress, setExportProgress] = useState<{ fetched: number; total: number } | null>(null);
    const [exportError, setExportError] = useState<string | null>(null);
    const accumulatedRows = useRef<JobApplicationRaw[]>([]);

    // Reset on open
    useEffect(() => {
        if (open) {
            setStep(0);
            setSelectedColumnIds(ALL_EXPORT_COLUMNS.map((c) => c.id));
            setExportLoading(false);
            setExportProgress(null);
            setExportError(null);
        }
    }, [open]);

    // Preview query — fires only on step 1
    const { data: previewQueryData, loading: previewLoading } = useQuery(queryToUse, {
        variables: {
            query: '',
            filter,
            sortBy: { field: 'date', order: -1 },
            pagination: { offset: 1, limit: 10 },
        },
        skip: step !== 1 || !open || !user,
        fetchPolicy: 'network-only',
    });

    const previewResult = extractResult(previewQueryData);
    const previewDocs: JobApplicationRaw[] = previewResult?.docs ?? [];
    const totalDocs: number = previewResult?.meta?.totalDocs ?? 0;

    // Lazy query for paginated export
    const [fetchPage] = useLazyQuery(queryToUse, { fetchPolicy: 'no-cache' });

    const handleExport = useCallback(async () => {
        setExportLoading(true);
        setExportError(null);
        setExportProgress({ fetched: 0, total: totalDocs });
        accumulatedRows.current = [];

        try {
            let offset = 0;

            // eslint-disable-next-line no-constant-condition
            while (true) {
                const result = await fetchPage({
                    variables: {
                        query: '',
                        filter,
                        sortBy: { field: 'date', order: -1 },
                        pagination: { offset, limit: EXPORT_PAGE_SIZE },
                    },
                });

                const pageData = extractResult(result.data);
                const docs: JobApplicationRaw[] = pageData?.docs ?? [];
                accumulatedRows.current = [...accumulatedRows.current, ...docs];

                setExportProgress({
                    fetched: accumulatedRows.current.length,
                    total: pageData?.meta?.totalDocs ?? totalDocs,
                });

                if (!pageData?.meta?.hasNextPage) break;
                offset++;
            }

            const filename = buildFilename(jobTitle, jobNumber);
            const rows = buildCsvRows(accumulatedRows.current, selectedColumnIds);
            downloadCsvFile(rows, filename);
        } catch {
            setExportError("Une erreur est survenue lors de l'export. Veuillez réessayer.");
        } finally {
            setExportLoading(false);
            setExportProgress(null);
        }
    }, [fetchPage, filter, extractResult, selectedColumnIds, jobTitle, jobNumber, totalDocs]);

    const allSelected = selectedColumnIds.length === ALL_EXPORT_COLUMNS.length;
    const noneSelected = selectedColumnIds.length === 0;

    const toggleColumn = (id: string) => {
        setSelectedColumnIds((prev) =>
            prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
        );
    };

    const toggleGroupAll = (group: ExportColumnGroup) => {
        const groupIds = ALL_EXPORT_COLUMNS.filter((c) => c.group === group).map((c) => c.id);
        const allGroupSelected = groupIds.every((id) => selectedColumnIds.includes(id));
        if (allGroupSelected) {
            setSelectedColumnIds((prev) => prev.filter((id) => !groupIds.includes(id)));
        } else {
            setSelectedColumnIds((prev) => Array.from(new Set([...prev, ...groupIds])));
        }
    };

    const toggleAll = () => {
        setSelectedColumnIds(allSelected ? [] : ALL_EXPORT_COLUMNS.map((c) => c.id));
    };

    const selectedCols = ALL_EXPORT_COLUMNS.filter((c) => selectedColumnIds.includes(c.id));

    const progressValue =
        exportProgress && exportProgress.total > 0
            ? (exportProgress.fetched / exportProgress.total) * 100
            : 0;

    return (
        <Dialog
            open={open}
            onClose={exportLoading ? undefined : onClose}
            disableEscapeKeyDown={exportLoading}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    background: theme.palette.mode === 'dark' ? '#1E2023' : theme.palette.background.paper,
                    backgroundImage: 'none',
                    overflow: 'hidden',
                },
            }}
        >
            {/* Unified header */}
            <Box
                sx={{
                    px: 3,
                    pt: 3,
                    pb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid rgba(255,255,255,0.07)`,
                }}
            >
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.25 }}>
                        Exporter les candidatures
                    </Typography>
                    {jobTitle && (
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {jobTitle}{jobNumber ? ` · #${jobNumber}` : ''}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <StepIndicator currentStep={step} />
                    <IconButton
                        size="small"
                        onClick={onClose}
                        disabled={exportLoading}
                        sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': { color: theme.palette.text.primary },
                        }}
                    >
                        <i className="fi fi-br-cross" style={{ fontSize: 12 }} />
                    </IconButton>
                </Box>
            </Box>

            {step === 0 ? (
                <>
                    <DialogContent sx={{ overflowY: 'auto' }}>
                        {/* Selection summary bar */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 2.5,
                                p: 1.5,
                                borderRadius: '10px',
                                background:
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(91, 72, 217, 0.1)'
                                        : 'rgba(91, 72, 217, 0.05)',
                                border: `1px solid rgba(91, 72, 217, 0.2)`,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                    Colonnes sélectionnées
                                </Typography>
                                <Chip
                                    label={`${selectedColumnIds.length} / ${ALL_EXPORT_COLUMNS.length}`}
                                    size="small"
                                    sx={{
                                        height: 22,
                                        fontWeight: 700,
                                        fontSize: 11,
                                        background: theme.palette.primary.main,
                                        color: theme.palette.primary.contrastText,
                                    }}
                                />
                            </Box>
                            <Button
                                size="small"
                                variant="text"
                                onClick={toggleAll}
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                    textTransform: 'none',
                                    px: 1,
                                    minWidth: 0,
                                }}
                            >
                                {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                            </Button>
                        </Box>

                        {/* Column groups */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {(Object.entries(EXPORT_COLUMN_GROUPS) as [ExportColumnGroup, string][]).map(
                                ([group, groupLabel]) => {
                                    const groupCols = ALL_EXPORT_COLUMNS.filter((c) => c.group === group);
                                    const selectedCount = groupCols.filter((c) =>
                                        selectedColumnIds.includes(c.id),
                                    ).length;
                                    const allGroupSelected = selectedCount === groupCols.length;
                                    const someGroupSelected = selectedCount > 0 && !allGroupSelected;

                                    return (
                                        <Box
                                            key={group}
                                            sx={{
                                                borderRadius: '12px',
                                                border: `1px solid`,
                                                borderColor:
                                                    selectedCount > 0
                                                        ? 'rgba(91, 72, 217, 0.3)'
                                                        : 'rgba(255,255,255,0.07)',
                                                overflow: 'hidden',
                                                transition: 'border-color 0.2s',
                                            }}
                                        >
                                            {/* Group header */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    px: 2,
                                                    py: 1.25,
                                                    background:
                                                        theme.palette.mode === 'dark'
                                                            ? 'rgba(255,255,255,0.04)'
                                                            : 'rgba(0,0,0,0.03)',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                }}
                                                onClick={() => toggleGroupAll(group)}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        {GROUP_ICONS[group]}
                                                    </Box>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 700, color: theme.palette.text.primary }}
                                                    >
                                                        {groupLabel}
                                                    </Typography>
                                                    <Chip
                                                        label={`${selectedCount}/${groupCols.length}`}
                                                        size="small"
                                                        sx={{
                                                            height: 18,
                                                            fontSize: 10,
                                                            fontWeight: 700,
                                                            background:
                                                                selectedCount > 0
                                                                    ? theme.palette.primary.main
                                                                    : 'rgba(255,255,255,0.06)',
                                                            color:
                                                                selectedCount > 0
                                                                    ? theme.palette.primary.contrastText
                                                                    : theme.palette.text.secondary,
                                                        }}
                                                    />
                                                </Box>
                                                <Checkbox
                                                    size="small"
                                                    checked={allGroupSelected}
                                                    indeterminate={someGroupSelected}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={() => toggleGroupAll(group)}
                                                    icon={<i className="fi fi-br-circle" style={{ fontSize: 16, display: 'flex', color: 'rgba(255,255,255,0.3)' }} />}
                                                    checkedIcon={
                                                        <i className="fi fi-br-check-circle" style={{ fontSize: 16, display: 'flex', color: theme.palette.primary.main }} />
                                                    }
                                                    sx={{ p: 0 }}
                                                />
                                            </Box>

                                            {/* Group columns */}
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr 1fr',
                                                    px: 2,
                                                    py: 1,
                                                    gap: 0.25,
                                                }}
                                            >
                                                {groupCols.map((col) => (
                                                    <FormControlLabel
                                                        key={col.id}
                                                        sx={{
                                                            m: 0,
                                                            py: 0.5,
                                                            borderRadius: '8px',
                                                            px: 0.5,
                                                            transition: 'background 0.15s',
                                                            '&:hover': {
                                                                background: 'rgba(255,255,255,0.04)',
                                                            },
                                                        }}
                                                        control={
                                                            <Checkbox
                                                                checked={selectedColumnIds.includes(col.id)}
                                                                onChange={() => toggleColumn(col.id)}
                                                                size="small"
                                                                sx={{ p: 0.5 }}
                                                            />
                                                        }
                                                        label={
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontSize: 13,
                                                                    color: selectedColumnIds.includes(col.id)
                                                                        ? theme.palette.text.primary
                                                                        : theme.palette.text.secondary,
                                                                }}
                                                            >
                                                                {col.label}
                                                            </Typography>
                                                        }
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    );
                                },
                            )}
                        </Box>
                    </DialogContent>

                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)' }} />
                    <DialogActions sx={{ gap: 1.5 }}>
                        <Button
                            onClick={onClose}
                            variant="text"
                            color="secondary"
                            sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2.5,
                            }}
                        >
                            Annuler
                        </Button>
                        <Button
                            variant="contained"
                            disabled={noneSelected}
                            onClick={() => setStep(1)}
                            endIcon={<i className="fi fi-br-table-list" style={{ fontSize: 13 }} />}
                            sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2.5,
                            }}
                        >
                            Aperçu
                        </Button>
                    </DialogActions>
                </>
            ) : (
                <>
                    <DialogContent>
                        {/* Summary info */}
                        {!previewLoading && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    mb: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Chip
                            icon={<i className="fi fi-br-users" style={{ fontSize: 12, display: 'flex' }} />}
                                    label={`${totalDocs} candidature${totalDocs !== 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{
                                        fontWeight: 700,
                                        background: 'rgba(91,72,217,0.15)',
                                        color: theme.palette.primary.main,
                                        border: '1px solid rgba(91,72,217,0.3)',
                                    }}
                                />
                                <Chip
                                    label={`${selectedCols.length} colonne${selectedCols.length !== 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{
                                        fontWeight: 600,
                                        background: 'rgba(255,255,255,0.06)',
                                        color: theme.palette.text.secondary,
                                        fontSize: 12,
                                    }}
                                />
                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, ml: 'auto' }}>
                                    Aperçu des 10 premières lignes
                                </Typography>
                            </Box>
                        )}

                        {/* Preview table */}
                        {previewLoading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 6,
                                    gap: 2,
                                }}
                            >
                                <CircularProgress size={36} />
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                    Chargement de l'aperçu…
                                </Typography>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    overflowX: 'auto',
                                    borderRadius: '12px',
                                    border: `1px solid rgba(255,255,255,0.07)`,
                                }}
                            >
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {selectedCols.map((col) => (
                                                <TableCell
                                                    key={col.id}
                                                    sx={{
                                                        fontWeight: 700,
                                                        whiteSpace: 'nowrap',
                                                        fontSize: 12,
                                                        background:
                                                            theme.palette.mode === 'dark'
                                                                ? '#2A2D30'
                                                                : theme.palette.background.paper,
                                                        color: theme.palette.text.secondary,
                                                        borderBottom: `1px solid rgba(255,255,255,0.08)`,
                                                        py: 1.25,
                                                        letterSpacing: '0.5px',
                                                        textTransform: 'uppercase',
                                                    }}
                                                >
                                                    {col.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {previewDocs.map((app, idx) => (
                                            <TableRow
                                                key={String(app._id ?? idx)}
                                                sx={{
                                                    '&:hover': {
                                                        background: 'rgba(255,255,255,0.03)',
                                                    },
                                                    '&:last-child td': { border: 0 },
                                                }}
                                            >
                                                {selectedCols.map((col) => (
                                                    <TableCell
                                                        key={col.id}
                                                        sx={{
                                                            whiteSpace: 'nowrap',
                                                            fontSize: 13,
                                                            borderBottom: `1px solid rgba(255,255,255,0.05)`,
                                                            color: theme.palette.text.primary,
                                                            maxWidth: 200,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {col.getValue(app) || (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ color: 'rgba(255,255,255,0.2)', fontStyle: 'italic' }}
                                                            >
                                                                —
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                        {previewDocs.length === 0 && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={selectedCols.length}
                                                    sx={{ textAlign: 'center', py: 4, color: theme.palette.text.secondary }}
                                                >
                                                    Aucune candidature trouvée
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}

                        {/* Export progress */}
                        {exportLoading && exportProgress && (
                            <Box
                                sx={{
                                    mt: 2.5,
                                    p: 2,
                                    borderRadius: '12px',
                                    background: 'rgba(91,72,217,0.08)',
                                    border: '1px solid rgba(91,72,217,0.2)',
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Export en cours…
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>
                                        {Math.round(progressValue)}%
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={progressValue}
                                    sx={{
                                        borderRadius: 4,
                                        height: 6,
                                        background: 'rgba(255,255,255,0.08)',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 4,
                                            background: theme.palette.primary.main,
                                        },
                                    }}
                                />
                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.75, display: 'block' }}>
                                    {exportProgress.fetched} / {exportProgress.total} candidatures récupérées
                                </Typography>
                            </Box>
                        )}

                        {exportError && (
                            <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>
                                {exportError}
                            </Alert>
                        )}
                    </DialogContent>

                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)' }} />
                    <DialogActions sx={{ gap: 1.5 }}>
                        <Button
                            onClick={() => setStep(0)}
                            disabled={exportLoading}
                            startIcon={<i className="fi fi-br-arrow-left" style={{ fontSize: 13 }} />}
                            variant="outlined"
                            color="inherit"
                            sx={{
                                borderColor: 'rgba(255,255,255,0.15)',
                                color: theme.palette.text.secondary,
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2.5,
                            }}
                        >
                            Retour
                        </Button>
                        <Button
                            variant="contained"
                            disabled={exportLoading || previewLoading || totalDocs === 0}
                            onClick={handleExport}
                            startIcon={
                                exportLoading ? (
                                    <CircularProgress size={15} color="inherit" />
                                ) : (
                                    <i className="fi fi-br-file-export" style={{ fontSize: 14 }} />
                                )
                            }
                            sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 700,
                                px: 3,
                                minWidth: 140,
                            }}
                        >
                            {exportLoading ? 'Export…' : 'Exporter CSV'}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default ExportJobApplicationsModal;
